import { DEVICE_SECTION } from 'core/constants';
import { isFailingSignal } from 'core/utils/predicates';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  Autocomplete,
  Badge,
  Box,
  Grid,
  Skeleton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { isEmptyOrUndefined } from '@zippeditoolsjs/blocks';

import { RobotViewContext } from '../../context/RobotView';
import {
  connectHost,
  disconnectHost,
  onConnect,
  onMessage,
  topicPublish,
  topicSubscribeMultiple,
  topicUnsubscribeMultiple,
} from '../../mqtt/mqttFunctions';
import ActionWidget from '../../redux/containers/widgets/ActionWidget';
import AlertsWidget from '../../redux/containers/widgets/AlertsWidget.js';
import HardwareAlertsWidget from '../../redux/containers/widgets/HardwareAlertsWidget.js';
import HardwareComponentsWidget from '../../redux/containers/widgets/HardwareComponentsWidget.js';
import NavigationWidget from '../../redux/containers/widgets/NavigationWidget';
import RobotParametersWidget from '../../redux/containers/widgets/RobotParametersWidget.js';
import RobotStateHistoryWidget from '../../redux/containers/widgets/RobotStateHistoryWidget.js';
import ScheduleWidget from '../../redux/containers/widgets/ScheduleWidget';
import ServicingHistoryWidget from '../../redux/containers/widgets/ServicingHistoryWidget.js';
import TimelineWidget from '../../redux/containers/widgets/TimelineWidget.js';
import { a11yProps } from '../Utils';
import ConfirmDialog from '../robotView/ConfirmDialog';
import NewServicingDialog from '../robotView/NewServicingDialog';
import TabPanelv2 from '../tools/TabPanelv2';
import AislesProgressWidget from '../widgets/AislesProgressWidget';
import CallerWidget from '../widgets/CallerWidget.js';
import CamWidget from '../widgets/CamWidget';
import MapWidget from '../widgets/MapWidget';
import PipelineProgressWidget from '../widgets/PipelineProgressWidget';
import { SessionLogsWidget } from '../widgets/SessionLogsWidget';
import SessionPlanWidget from '../widgets/SessionPlanWidget';
import NewStateDialog from './NewStateDialog.js';

const { REACT_APP_MQTT_USER, REACT_APP_MQTT_PASSWORD } = process.env;

// Joins device locators into a chart signal name. The overseer-api normalizer
// builds the same name from the health_status logs, so this format is a
// cross-repo contract — it MUST match `_build_signal_name` in normalizers.py.
const buildSignalName = (section, group, instance, metric) =>
  `${section}.${group}.${instance}.${metric}`;

// Flattens named device sections and top-level booleans into modal tab data and
// flags failing signals for the card status icon. Each metric carries its
// reconstructed chart signal name (statusKey) for the history lookup.
const buildStatusTabs = (status) => {
  const devices = [];
  const extra = [];

  Object.values(DEVICE_SECTION).forEach((section) => {
    const sectionValue = status[section];
    if (!sectionValue || typeof sectionValue !== 'object') return;
    Object.entries(sectionValue).forEach(([group, instances]) => {
      if (!Array.isArray(instances)) return;
      const groupInstances = instances
        .filter((instance) => instance && typeof instance === 'object')
        .map((instance) => ({
          name: instance.name,
          metrics: Object.entries(instance)
            .filter(([metric]) => metric !== 'name')
            .map(([metric, metricValue]) => ({
              section,
              group,
              instance: instance.name,
              metric,
              value: metricValue,
              statusKey: buildSignalName(section, group, instance.name, metric),
            })),
        }));
      devices.push({ section, group, instances: groupInstances });
    });
  });

  // Top-level booleans feed the Extra tab.
  Object.entries(status).forEach(([key, value]) => {
    if (typeof value !== 'boolean') return;
    extra.push({ name: key, value });
  });

  // Failing = a boolean signal that is false, except is_enabled (a disabled
  // device is not a failure). Drives sort order and the status icon.
  const isFailing = (instance) =>
    instance.metrics.some((metric) => isFailingSignal(metric.metric, metric.value));
  const failingFirst = (a, b) => (a === b ? 0 : a ? -1 : 1);

  devices.sort(
    (a, b) =>
      failingFirst(a.instances.some(isFailing), b.instances.some(isFailing)) ||
      a.group.localeCompare(b.group)
  );
  devices.forEach((deviceGroup) => {
    deviceGroup.instances.sort(
      (a, b) =>
        failingFirst(isFailing(a), isFailing(b)) || a.name.localeCompare(b.name)
    );
    deviceGroup.instances.forEach((instance) =>
      instance.metrics.sort((a, b) => a.metric.localeCompare(b.metric))
    );
  });
  extra.sort((a, b) => a.name.localeCompare(b.name));

  const hasFailing =
    devices.some((group) => group.instances.some(isFailing)) ||
    extra.some((item) => isFailingSignal(item.name, item.value));

  const hasSignals = devices.length > 0 || extra.length > 0;
  let status_ = 'OK';
  if (hasFailing) status_ = 'Error';
  else if (!hasSignals) status_ = 'NoData';

  return { tabs: { devices, extra }, status: status_ };
};

// TODO(deprecate): remove with legacy status format.
const LEGACY_IMPORTANT_INFO = [
  'nav',
  'collector1',
  'collector2',
  'collector3',
  'battery',
];

// TODO(deprecate): remove with legacy status format.
// Parses legacy-shaped health data into Legacy-tab rows and a status. Top-level
// booleans are skipped — the Extra tab owns them — so this keeps only nested
// component data (e.g. collector dicts). Returns 'NoData' when the payload
// carries no legacy-shaped data at all.
const buildLegacyStatus = (status) => {
  const componentRows = {};
  LEGACY_IMPORTANT_INFO.forEach((info) => {
    componentRows[info] = [];
  });

  Object.entries(status).forEach(([key, value]) => {
    if (Object.prototype.toString.call(value) === '[object Object]') {
      Object.keys(value).forEach((subKey) => {
        if (LEGACY_IMPORTANT_INFO.includes(subKey)) {
          const health =
            value[subKey] !== null && typeof value[subKey] === 'object'
              ? value[subKey]['is_healthy'] ?? value[subKey]
              : value[subKey];
          componentRows[subKey].push([key, health]);
        }
      });
    } else if (LEGACY_IMPORTANT_INFO.includes(key) && typeof value !== 'boolean') {
      componentRows[key] = [[key, value]];
    }
  });

  const hasData = Object.values(componentRows).some((rows) => rows.length > 0);

  const errors = [];
  const others = [];
  Object.entries(componentRows).forEach(([name, rows]) => {
    if (rows.length === 1 && rows[0][1] === false) errors.push({ name, data: rows });
    else others.push({ name, data: rows });
  });

  let status_ = 'OK';
  if (!hasData) {
    status_ = 'NoData';
  } else {
    const allHealthy = LEGACY_IMPORTANT_INFO.every((info) =>
      componentRows[info].every((row) => row[1] !== false)
    );
    if (!allHealthy) {
      const anyMissing = LEGACY_IMPORTANT_INFO.some(
        (info) => componentRows[info].length === 0
      );
      status_ = anyMissing ? 'Warning' : 'Error';
    }
  }

  // Empty rows are noise when there is no legacy data; show the empty state instead.
  return { detail: hasData ? [...errors, ...others] : [], status: status_ };
};

// Worst-of-both card status across the legacy and device sources. A source with
// no data ('NoData') has no opinion; only when neither reports does it warn.
const STATUS_RANK = { NoData: 0, OK: 1, Warning: 2, Error: 3 };
const combineStatus = (...statuses) => {
  const worst = statuses.reduce((a, b) =>
    STATUS_RANK[a] >= STATUS_RANK[b] ? a : b
  );
  return worst === 'NoData' ? 'Warning' : worst;
};

/**
 * get all possible topics for a robot
 * @param  	{string} 				store 				store code (e.g. J510)
 * @param  	{string}				robot 				robot number (e.g. 1)
 * @param 	{object}				args					optional arguments
 * @param 	{string}				args.topic_id	optional topic id to get a specific topic from all topics available in this view (e.g. nav_topic)
 * @param 	{boolean}				args.canParse	optional boolean to get all topics in a format that can be parsed by subscribeMultiple function from mqttFunctions.js
 * @return  {object}        			 				object with desired topics
 */
export function topics_handler(
  store,
  robot,
  args = {
    topic_id: null,
    canParse: false,
  }
) {
  const TOPICS_INFO = {
    nav_topic: { topic: `info/${store}/${robot}/nav_data`, qos: 0 },
    map_topic: {
      topic: `info/${store}/${robot}/map`,
      qos: 1,
      publishTopic: `info/${store}/${robot}/get_map`,
      publishQos: 1,
    },
    frontal_image: { topic: `info/${store}/${robot}/frontal`, qos: 0 },
    floor_image: { topic: `info/${store}/${robot}/floor`, qos: 0 },
    side_cameras: {
      topic: `info/${store}/${robot}/side_cameras`,
      qos: 0,
      publishTopic: `info/${store}/${robot}/get_side_cameras`,
      publishQos: 0,
    },
    security_cameras: {
      topic: `info/${store}/${robot}/security_cameras`,
      qos: 0,
      publishTopic: `info/${store}/${robot}/get_security_cameras`,
      publishQos: 0,
    },
    info_command: {
      topic: `info/${store}/${robot}/command_exec_status`,
      qos: 2,
      publishTopic: `info/${store}/${robot}/command`,
      publishQos: 2,
    },
    footage_topic: {
      topic: `info/${store}/${robot}/footage/response`,
      qos: 2,
      publishTopic: `info/${store}/${robot}/footage`,
      publishQos: 2,
    },
    autonomous_mapping: {
      topic: `${store}/${robot}/lines/response`,
      qos: 2,
      publishTopic: `${store}/${robot}/lines/request`,
      publishQos: 2,
    },
    robot_status_topic: {
      topic: `info/${store}/${robot}/robot_status`,
      qos: 0,
    },
    refresh_robot_status_topic: {
      topic: `info/${store}/${robot}/refresh_robot_status`,
      qos: 0,
    },
    connection_status_topic: {
      topic: `info/${store}/${robot}/connection_status`,
      qos: 0,
    },
    thumb_map_topic: {
      topic: `info/${store}/${robot}/thumb_map`,
      qos: 0,
    },
    monitoring_logs: {
      topic: `info/${store}/${robot}/monitoring_logs`,
      qos: 0,
      publishTopic: `info/${store}/${robot}/get_monitoring_logs`,
      publishQos: 1,
    },
  };

  if (args.canParse) {
    return Object.values(TOPICS_INFO).reduce((beforeValue, currentValue) => {
      return {
        ...beforeValue,
        [currentValue.topic]: { qos: currentValue.qos },
      };
    }, {});
  } else {
    return TOPICS_INFO;
  }
}

const RobotViewSkeleton = ({
  // Animation type
  animation = 'wave',
  // Colors
  backgroundColor = '#fff',
  skeletonColor = '#f7f6fa',
  // Spacing
  containerPadding = 3,
  containerSpacing = 2,
  cardPadding = 2,
  // Heights
  statusCardHeight = 50,
  bottomCardHeight = 400,
  // Border radius
  borderRadius = 1,
  // Text widths
  titleWidth = '60%',
  bottomTitleWidth = '40%',
  // Grid layout
  middleCardsGridSize = 4,
}) => {
  const commonBoxProps = {
    sx: {
      background: backgroundColor,
      p: cardPadding,
      borderRadius,
    },
  };

  const commonSkeletonProps = {
    animation,
    sx: {
      borderRadius,
      bgcolor: skeletonColor,
    },
  };

  return (
    <Grid container spacing={containerSpacing} sx={{ p: containerPadding }}>
      {/* Status Section */}
      <Grid item xs={12}>
        <Box {...commonBoxProps}>
          <Grid container spacing={containerSpacing}>
            <Grid item xs={12}>
              <Skeleton
                {...commonSkeletonProps}
                variant="rectangular"
                height={statusCardHeight}
              />
            </Grid>
          </Grid>
        </Box>
      </Grid>

      {/* Middle Cards Section */}
      {[1, 2, 3].map((index) => (
        <Grid key={index} item xs={12} md={middleCardsGridSize}>
          <Box {...commonBoxProps} sx={{ ...commonBoxProps.sx }}>
            <Skeleton
              {...commonSkeletonProps}
              variant="text"
              width={titleWidth}
              height={30}
              sx={{ ...commonSkeletonProps.sx, mb: 2 }}
            />
            <Skeleton
              {...commonSkeletonProps}
              variant="rectangular"
              height={250}
            />
          </Box>
        </Grid>
      ))}

      {/* Bottom Section */}
      <Grid item xs={8}>
        <Box {...commonBoxProps}>
          <Skeleton
            {...commonSkeletonProps}
            variant="text"
            width={bottomTitleWidth}
            height={30}
            sx={{ ...commonSkeletonProps.sx, mb: 2 }}
          />
          <Skeleton
            {...commonSkeletonProps}
            variant="rectangular"
            height={bottomCardHeight}
          />
        </Box>
      </Grid>
      <Grid item xs={4}>
        <Box {...commonBoxProps}>
          <Skeleton
            {...commonSkeletonProps}
            variant="text"
            width={bottomTitleWidth}
            height={30}
            sx={{ ...commonSkeletonProps.sx, mb: 2 }}
          />
          <Skeleton
            {...commonSkeletonProps}
            variant="rectangular"
            height={bottomCardHeight}
          />
        </Box>
      </Grid>
    </Grid>
  );
};

export default function RobotView(props) {
  const navigate = useNavigate();
  const mqttOptions = {
    username: REACT_APP_MQTT_USER,
    password: REACT_APP_MQTT_PASSWORD,
  };
  let { store: storeRaw, robot: robotRaw } = useParams();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialTab = parseInt(searchParams.get('tab'), 10) || 0;

  const {
    robotActionsList,
    getRobotActions,
    topicMessageFeedback,
    postUserActions,
    getRobotsTaken,
    robotsTakenList,
    takeRobot,
    isLoadingTakeRobot,
    leaveRobot,
    isLoadingLeaveRobot,
    takeRobotResponse,
    leaveRobotResponse,
    getRobotBlocking,
    isLoadingRobotBlocking,
    postNewServicing,
    isLoadingNewServicing,
    newServicingResponse,
    getChangeMotives,
    changeMotivesList,
    isLoadingChangeMotives,
    robotComponentsList,
    getRobotComponents,
    setSnackAlert,
    productsList,
    isLoadingProductsList,
    getProductsByName,
    getBroker,
    brokerUrl,
    isLoadingBrokerUrl,
    getActiveAlerts,
    postNewMap,
    storeMapInfo,
    getStoreMap,
    getStoreLayout,
    storeInfo,
    isLoadingStoreInfo,
    getStore,
    userInfo,
    getUserInfo,
    detailedUserInfo,
    activeAlerts,
    getMapSignal,
    getMappingAlerts,
    mapSignal,
    isLoadingMapSignalInfo,
    featurePermission,
    checkFeaturePermission,
    getRobotState,
    robotStateInfo,
    getAvailableRobotStatus,
    availableStatus,
    updateRobotState,
    loadingNewRobotState,
    robotStateUpdateResponse,
    getRobotStateMotives,
    robotStateMotives,
    hardwareActiveAlerts,
    getActiveHardwareAlerts,
    postActionReason,
    getCallerHistory,
    postCallerAction,
    callerHistory,
    newCallerActionResponse,
    loadingNewCallerActionResponse,
    storeCountry,
    getCountryByStore,
    getCallMotives,
    callMotives,
    linkReceived,
    mappingAlerts,
    isMappingAlertsLoading,
    robotsByStatus,
    isLoadingRobotsByStatus,
    getRobotsByStatus,
    getSessionsState,
    robotsListBaldur,
    robotSessionState,
    getRobotStateHistory,
    storeLayout,
    isLoadingStoreLayout
  } = props;

  const { t } = useTranslation();
  const {
    setVirtualBorders,
    setIsLinesLoading,
    setOnDemandCamerasOptions,
    rawRobotStatus,
    setRawRobotStatus,
    setStatusTabs,
    isSubscribedStatus,
    setIsSubscribedStatus,
    setConnectionStatusMsg,
    robotFloor,
    setRobotFloor,
  } = useContext(RobotViewContext);

  const [store, setStore] = useState(storeRaw?.toUpperCase());
  const [robot, setRobot] = useState();
  const [topics, setTopics] = useState({});
  const [client, setClient] = useState(null);
  const [tabIndex, setTabIndex] = useState(initialTab);
  const [mqttMessage, setMqttMessage] = useState({});
  const [mqttNavMessage, setNavMqttMessage] = useState({});
  const [mqttNavInfo, setMqttNavInfo] = useState({});
  const [frontalImageMsg, setFrontalImageMsg] = useState({});
  const [frontalImage, setFrontalImage] = useState({});
  const [floorImageMsg, setFloorImageMsg] = useState({});
  const [floorImage, setFloorImage] = useState({});
  const [robotMainStatus, setRobotMainStatus] = useState(null);
  const [robotDetailStatus, setRobotDetailStatus] = useState(null);
  const [thumbMap, setThumbMap] = useState(null);
  const [monitoringLogs, setMonitoringLogs] = useState(null);
  const [isSessionLogsLoading, setIsSessionLogsLoading] = useState(false);
  const [intervalId, setIntervalId] = useState(1);
  const [open, setOpen] = useState(false);
  const [resetForm, setResetForm] = useState(false);
  const [iHaveRobotTaken, setIHaveRobotTaken] = useState(false);
  const [controlledBy, setControlledBy] = useState(null);
  const [openKick, setOpenKick] = useState(false);
  const [canSendHeartbeat, setCanSendHeartbeat] = useState(false);
  const [isRobotTaken, setIsRobotTaken] = useState(false);
  const [mapHeight, setMapHeight] = useState('27rem');
  const [robotState, setRobotState] = useState(null);
  const [openState, setOpenState] = useState(false);
  const [storeLang, setStoreLang] = useState(null);
  const [selectedRobot, setSelectedRobot] = useState(null);

  const handleRobotChange = (event, newValue) => {
    if (newValue?.code) {
      const [store, robot] = newValue.code.split('-');
      navigate(`/robot/${store}/${robot}`);
    }
  };

  const handleOpenDialog = () => setOpen(true);
  const handleCloseDialog = () => setOpen(false);
  const handleUnsubscribe = () => {
    const beforeTopicsParsed = Object.values(topics).reduce(
      (beforeValue, currentValue) => {
        return {
          ...beforeValue,
          [currentValue.topic]: { qos: currentValue.qos },
        };
      },
      {}
    );
    onConnect(client, () =>
      topicUnsubscribeMultiple(client, beforeTopicsParsed, () => { })
    );
    disconnectHost(client);
    setClient(null);
    setIsSubscribedStatus(false);
  };

  // Hooks
  useEffect(() => {
    if (robotsByStatus && store && robot) {
      const robotCode = `${store}-${robot}`;
      const defaultRobot = robotsByStatus.find((r) => r.code === robotCode);
      if (defaultRobot) {
        setSelectedRobot(defaultRobot);
      }
    }
  }, [robotsByStatus, store, robot]);

  useEffect(() => {
    getCallMotives();
    getRobotsByStatus();
    getRobotStateMotives();
    if (userInfo.email) getUserInfo(userInfo.email);
  }, []);

  useEffect(() => {
    if (store) {
      getCallerHistory(store);
      getCountryByStore(store);
    }
  }, [store]);

  useEffect(() => {
    if (storeInfo) {
      document.title = `${storeInfo.code}: ${storeInfo.name}`;
    }
    return () => {
      document.title = 'Overseer App';
    };
  }, [storeInfo]);

  useEffect(() => {
    if (storeRaw) {
      getStore(storeRaw);
      setStore(storeRaw.toUpperCase());
    }
    if (robotRaw !== undefined) {
      setRobot(robotRaw);
    } else {
      setRobot(1);
    }
  }, [storeRaw, robotRaw, selectedRobot]);

  useEffect(() => {
    if (canSendHeartbeat) {
      setCanSendHeartbeat(false);
      publishHeartbeat();
    }
  }, [canSendHeartbeat]);

  useEffect(() => {
    if (brokerUrl && !isLoadingBrokerUrl) {
      if (!client) {
        const clientConnection = connectHost(brokerUrl, mqttOptions);
        setClient(clientConnection);
      } else if (!isSubscribedStatus) {
        const topicsParsed = topics_handler(store, robot, { canParse: true });
        onConnect(client, () =>
          topicSubscribeMultiple(client, topicsParsed, () =>
            setIsSubscribedStatus(true)
          )
        );
        client.on('error', (err) => {
          console.log('error', err);
          setIsSubscribedStatus(false);
        });
      }
    } else if (isLoadingBrokerUrl) {
      if (client) {
        handleUnsubscribe();
      } else {
        setIsSubscribedStatus(false);
      }
    }
  }, [brokerUrl, client, isLoadingBrokerUrl]);

  useEffect(() => {
    let newInterval, heartBeatInterval, robotCode;
    setRobotFloor(null);
    if (store) {
      if (robot !== undefined) {
        if (intervalId !== null) clearInterval(intervalId);
        newInterval = handleRobotTaken();
        heartBeatInterval = handleHeartBeat();
        robotCode = `${store}-${robot}`;
        getRobotComponents(robotCode);
        getStoreMap({ robot_code: robotCode });
        getRobotState(robotCode);
        getActiveHardwareAlerts(robotCode);
        postUserActions('enter-new-robot-page', robotCode);
      }
      getBroker(store);
    }
    return () => {
      clearInterval(newInterval);
      clearInterval(heartBeatInterval);
      clearInterval(intervalId);
    };
  }, [robot, store]);

  useEffect(() => {
    if (store && (robotFloor !== null && robotFloor !== undefined) && !isNaN(robotFloor)) {
      getStoreLayout({ store, floor: robotFloor });
    }
  }, [store, robotFloor]);

  useEffect(() => {
    if (mqttNavMessage.msg) {
      setMqttNavInfo(JSON.parse(mqttNavMessage.msg));
    }
  }, [mqttNavMessage]);

  useEffect(() => {
    if (frontalImageMsg.msg) {
      setFrontalImage(JSON.parse(frontalImageMsg.msg));
    }
  }, [frontalImageMsg]);

  useEffect(() => {
    if (floorImageMsg.msg) {
      setFloorImage(JSON.parse(floorImageMsg.msg));
    }
  }, [floorImageMsg]);

  useEffect(() => {
    getRobotActions();
    getChangeMotives();
  }, [getRobotActions]);

  useEffect(() => {
    getRobotsTaken(`${store}-${robot}`);
  }, [isLoadingLeaveRobot]);

  useEffect(() => {
    if (newServicingResponse && newServicingResponse.status === 200) {
      handleCloseDialog();
      setResetForm(true);
    }
  }, [newServicingResponse]);

  useEffect(() => {
    if (robotStateUpdateResponse === 200) {
      setOpenState(false);
      setResetForm(true);
      getRobotState(`${store}-${robot}`);
      getRobotStateHistory(`${store}-${robot}`);
    }
  }, [robotStateUpdateResponse]);

  useEffect(() => {
    if (isSubscribedStatus) {
      onMessage(client, msgHandler);
    }
  }, [isSubscribedStatus]);

  useEffect(() => {
    if (robotsTakenList && robotsTakenList[`${store}-${robot}`]) {
      if (robotsTakenList[`${store}-${robot}`].user === null) {
        setControlledBy(null);
        setIHaveRobotTaken(true);
      } else if (robotsTakenList[`${store}-${robot}`].user.username) {
        setControlledBy(robotsTakenList[`${store}-${robot}`].user.username);
        setIHaveRobotTaken(false);
      }
      setIsRobotTaken(true);
    } else {
      setControlledBy(null);
      setIHaveRobotTaken(false);
      setIsRobotTaken(false);
    }
  }, [robotsTakenList]);

  useEffect(() => {
    if (takeRobotResponse && takeRobotResponse.error === false) {
      postUserActions('take-robot', `${store}-${robot}`);
      setIHaveRobotTaken(true);
      getRobotsTaken(`${store}-${robot}`);
    }
  }, [takeRobotResponse]);

  useEffect(() => {
    if (leaveRobotResponse && leaveRobotResponse.error === false) {
      postUserActions('leave-robot', `${store}-${robot}`);
      setIHaveRobotTaken(false);
    }
  }, [leaveRobotResponse]);

  useEffect(() => {
    publishHeartbeat();
  }, [isRobotTaken]);

  useEffect(() => {
    if (robotStateInfo) {
      setRobotState(robotStateInfo.status_id);
    }
  }, [robotStateInfo]);

  useEffect(() => {
    if (storeCountry?.locale) {
      setStoreLang(storeCountry.locale.split('_')[0]);
    }
  }, [storeCountry]);

  // Redirects from Dashboards
  useEffect(() => {
    if (!isEmptyOrUndefined(linkReceived, 'object')) {
      setTabIndex(linkReceived?.tabIndex);
    }
  }, [linkReceived]);

  // Handlers
  const handleCommandsResponse = (msg) => {
    let data = JSON.parse(msg['msg']);
    if (data.command_exec_state) {
      topicMessageFeedback(data.message, data.command_exec_state);
      if (data.command_exec_state === 'success') {
        setMqttMessage(data);
      }
    }
  };

  const handleRobotTaken = () => {
    let interval = setInterval(
      () => getRobotsTaken(`${store}-${robot}`),
      60000
    );
    setIntervalId(interval);
    setTopics(topics_handler(store, robot));
    getRobotsTaken(`${store}-${robot}`);
    return interval;
  };

  const handleHeartBeat = () => {
    let interval = setInterval(() => setCanSendHeartbeat(true), 270000);
    return interval;
  };

  const publishHeartbeat = () => {
    if (client) {
      topicPublish(client, {
        topic: `info/${store}/${robot}/heartbeat`,
        qos: 0,
        payload: JSON.stringify({
          isTaken: isRobotTaken,
          robotState: robotState,
        }),
      });
    }
  };

  const handleMapInfo = (mapMsg) => {
    // If map comes in the payload, then it is a map update
    let mapPayload = {};
    try {
      mapPayload = JSON.parse(mapMsg.msg);
    } catch (error) {
      console.log('Error parsing map payload', mapMsg?.msg, error);
    }

    if (mapPayload?.map) {
      // Publish the recently arrived map and its hash to the database
      const map_bytes = mapPayload['map'];
      const map_metadata = mapPayload['map_metadata'];
      const map_hash = mapPayload['hash'];

      const fileForm = new FormData();
      fileForm.append('img', map_bytes);
      fileForm.append('robot_code', `${store}-${robot}`);
      fileForm.append('hash', map_hash);
      fileForm.append('resolution', map_metadata['resolution']);
      fileForm.append('height', map_metadata['height']);
      fileForm.append('width', map_metadata['width']);
      fileForm.append('origin', JSON.stringify(map_metadata['origin']));

      postNewMap({ fileForm });
    }
  };

  const handleFootageResponse = (msg) => {
    // If map comes in the payload, then it is a map update
    let payload = {};
    try {
      payload = JSON.parse(msg.msg);
    } catch (error) {
      console.log('Error parsing map payload', msg?.msg, error);
    }
    if (payload['message'] === 'success') {
      const snack = {
        open: true,
        message:
          'Successfully sent footage form to the backend, you will receive an email with the footage soon',
        severity: 'success',
      };
      setSnackAlert(snack);
    } else {
      const snack = {
        open: true,
        message: 'Error sending footage to the backend, try again later',
        severity: 'error',
      };
      setSnackAlert(snack);
    }
  };

  const handleRobotStatusResponse = (msg) => {
    try {
      const status = JSON.parse(msg['msg']);

      // Always parse both shapes so every tab with data shows, regardless of
      // format. The card status is the worst of the two sources.
      const { tabs, status: deviceStatus } = buildStatusTabs(status);
      const { detail, status: legacyStatus } = buildLegacyStatus(status);

      setStatusTabs(tabs);
      setRobotDetailStatus(detail);
      setRobotMainStatus(combineStatus(deviceStatus, legacyStatus));
      setRawRobotStatus(status);
      setRobotFloor(status?.floor_number);
    } catch (error) {
      console.log('Error parsing map payload', msg?.msg, error);
    }
  };

  const handleAutonomousMapping = (msg) => {
    let payload = {};
    try {
      payload = JSON.parse(msg.msg);
    } catch (error) {
      console.log('Error parsing map payload', msg?.msg, error);
    }
    if (payload['command_exec_state'] === 'success') {
      if (payload['message'] === 'Dictionary with lines retrieved') {
        setVirtualBorders(
          Object.entries(payload['info']).map(([key, value]) => {
            return {
              id: key,
              pxInit: { x: value[0][0], y: value[0][1] },
              pxEnd: { x: value[1][0], y: value[1][1] },
            };
          })
        );
      } else {
        const snack = {
          open: true,
          message: payload['message'],
          severity: 'success',
        };
        setSnackAlert(snack);
      }
    } else {
      const snack = {
        open: true,
        message: 'Error sending virtual borders to the robot, try again later',
        severity: 'error',
      };
      setSnackAlert(snack);
    }
    setIsLinesLoading(false);
  };

  const handleRefreshRobotAlert = (msg) => {
    getActiveAlerts({ robot_id: `${store}-${robot}` });
  };

  const handleSetOnDemandCameras = (cameraType) => (msg) => {
    let payload = {};
    try {
      payload = JSON.parse(msg.msg);
    } catch (error) {
      console.log('Error parsing map payload', msg?.msg, error);
    }
    setOnDemandCamerasOptions({ [cameraType]: payload });
  };

  const handleConnectionStatus = (msg) => {
    setConnectionStatusMsg(JSON.parse(msg['msg']));
  };

  const handleThumbMap = (msg) => {
    let message = JSON.parse(msg['msg']);
    setThumbMap(message['img']);
  };

  const handleMonitoringLogs = (msg) => {
    let payload = {};
    try {
      payload = JSON.parse(msg.msg);
    } catch (error) {
      console.log('Error parsing map payload', msg?.msg, error);
    }
    setIsSessionLogsLoading(false);
    setMonitoringLogs((prevLogs) => {
      return { ...prevLogs, [payload.log_type]: payload?.log };
    });
  };

  const msgHandler = (payload) => {
    const topicMapper = {
      [topics.map_topic?.topic]: handleMapInfo,
      [topics.nav_topic?.topic]: setNavMqttMessage,
      [topics.footage_topic?.topic]: handleFootageResponse,
      [topics.frontal_image.topic]: setFrontalImageMsg,
      [topics.floor_image?.topic]: setFloorImageMsg,
      [topics.info_command?.topic]: handleCommandsResponse,
      [topics.autonomous_mapping?.topic]: handleAutonomousMapping,
      [topics.robot_status_topic?.topic]: handleRobotStatusResponse,
      [topics.refresh_robot_status_topic?.topic]: handleRefreshRobotAlert,
      [topics.side_cameras?.topic]: handleSetOnDemandCameras('Collector'),
      [topics.security_cameras?.topic]: handleSetOnDemandCameras('Security'),
      [topics.connection_status_topic?.topic]: handleConnectionStatus,
      [topics.thumb_map_topic?.topic]: handleThumbMap,
      [topics.monitoring_logs?.topic]: handleMonitoringLogs,
    };
    if (topicMapper[payload?.topic]) topicMapper[payload.topic](payload);
  };

  const handleChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const takeSelectedRobot = () => {
    getRobotsTaken();
    takeRobot(`${store}-${robot}`);
  };

  const leaveSelectedRobot = () => {
    getRobotsTaken();
    leaveRobot(`${store}-${robot}`);
  };

  const kickAndTake = () => {
    takeRobot(`${store}-${robot}`, undefined, true);
    setOpenKick(false);
  };

  const handleCloseKickDialog = () => {
    setOpenKick(false);
  };

  async function publishMessageCommand(element, extraInfo = null) {
    let cmdPayload = { command: element.command };
    if (extraInfo) {
      cmdPayload = { ...cmdPayload, ...extraInfo };
    }
    let response = await topicPublish(client, {
      topic: topics.info_command.publishTopic,
      qos: topics.info_command.publishQos,
      payload: JSON.stringify(cmdPayload),
    });
    if (response && response.status) {
      topicMessageFeedback(response.message, response.status);
    }
  }

  return (
    <Grid container>
      <Grid
        item
        xs={12}
        sx={{ borderRadius: '15px', background: '#f7f6fa', pb: 2 }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 2, padding: 1 }}
          >
            {/* Robot Selector */}
            <Autocomplete
              sx={{ minWidth: 200 }}
              size="small"
              options={robotsByStatus || []}
              getOptionLabel={(option) => option.code || ''}
              groupBy={(option) => option.chain || 'Other'}
              value={selectedRobot}
              onChange={handleRobotChange}
              isOptionEqualToValue={(option, value) =>
                option.code === value?.code
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t(
                    'overseer_app.robot_view.select_robot',
                    'Select a robot'
                  )}
                  placeholder={t(
                    'overseer_app.robot_view.type_to_search',
                    'Type to search...'
                  )}
                />
              )}
              renderGroup={(params) => (
                <li key={params.key}>
                  <Box
                    sx={{
                      position: 'sticky',
                      top: -8,
                      zIndex: 1,
                      backgroundColor: '#f5f5f5',
                      p: 1,
                    }}
                  >
                    <Typography variant="caption" color="textSecondary">
                      {params.group}
                    </Typography>
                  </Box>
                  {params.children}
                </li>
              )}
              loading={isLoadingRobotsByStatus}
              loadingText={t(
                'overseer_app.robot_view.loading_robots',
                'Loading robots...'
              )}
              noOptionsText={t(
                'overseer_app.robot_view.no_robots_found',
                'No robots found'
              )}
            />
            {/* Chain name and store location */}
            <Box
              sx={{
                display: 'flex',
                minWidth: 50,
                minHeight: '2em',
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
            >
              <Typography
                variant="body2"
                component="h1"
                sx={{ color: '#000000b3', fontWeight: 600 }}
              >
                {isLoadingStoreInfo
                  ? '...'
                  : selectedRobot?.chain === ''
                    ? t('overseer_app.robot_view.no_chain', 'No chain')
                    : selectedRobot?.chain
                      ? selectedRobot?.chain
                      : ''}
              </Typography>
              <Typography
                variant="body2"
                component="h1"
                sx={{ color: '#000000b3', fontWeight: 500 }}
              >
                {isLoadingStoreInfo
                  ? '...'
                  : storeInfo?.name === ''
                    ? t('overseer_app.robot_view.no_store', 'No store')
                    : storeInfo?.name
                      ? storeInfo?.name
                      : ''}
              </Typography>
            </Box>
          </Box>

          <Tabs
            value={tabIndex}
            onChange={handleChange}
            aria-label="basic tabs example"
            style={{ marginLeft: 'auto' }}
          >
            <Tab label="Monitoring" {...a11yProps(0, {})} />
            <Tab
              disabled={!(store && robot) || isLoadingBrokerUrl}
              label="Session"
              {...a11yProps(1, {})}
            />
            <Tab
              disabled={!(store && robot) || isLoadingBrokerUrl}
              label="Timeline"
              {...a11yProps(2, {})}
            />
            <Tab
              disabled={!(store && robot) || isLoadingBrokerUrl}
              label={
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  Maintenance
                  <Badge
                    badgeContent={
                      hardwareActiveAlerts.length > 0
                        ? hardwareActiveAlerts.length
                        : null
                    }
                    color="error"
                    sx={{
                      position: 'absolute',
                      right: '-5px',
                      opacity: 0.8,
                      pointerEvents: 'none',
                    }}
                  />
                </Box>
              }
              {...a11yProps(3, {})}
            />
          </Tabs>
        </Box>
        {/* Monitoring Widgets */}
        <TabPanelv2 value={tabIndex} index={0} sx={{ p: 3 }}>
          {isLoadingBrokerUrl ? (
            <RobotViewSkeleton animation="wave" containerPadding={0} />
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <NavigationWidget
                  title={t('overseer_app.widget.card_robot_status', 'Status')}
                  mqttNavInfo={mqttNavInfo}
                  thumbMap={thumbMap}
                  storeMapInfo={storeMapInfo}
                  store={store}
                  robot={robot}
                  robotMainStatus={robotMainStatus}
                  robotDetailStatus={robotDetailStatus}
                  rawData={rawRobotStatus}
                  activeAlerts={activeAlerts}
                  robotState={robotState}
                  isNoRobotSelected={!(store && robot)}
                />
              </Grid>
              <Grid item sm={12} md={4.5}>
                <CamWidget
                  name={'Frontal'}
                  image={frontalImage}
                  isNoRobotSelected={!(store && robot)}
                />
              </Grid>
              <Grid item sm={12} md={4}>
                <CamWidget
                  name={'Floor'}
                  image={floorImage}
                  isNoRobotSelected={!(store && robot)}
                />
              </Grid>
              <Grid item sm={12} md={3.5}>
                <AlertsWidget
                  store={store}
                  robot={robot}
                  isNoRobotSelected={!(store && robot)}
                />
              </Grid>
              <Grid item sm={12} md={8}>
                <MapWidget
                  id="robot-view"
                  title={t(`overseer_app.widget.map`, 'Map')}
                  robot={robot}
                  storeInfo={storeInfo}
                  mqttNavInfo={mqttNavInfo}
                  storeMapInfo={storeMapInfo}
                  minHeight={mapHeight}
                  getMapSignal={getMapSignal}
                  getMappingAlerts={getMappingAlerts}
                  mapSignal={mapSignal}
                  isLoadingMapSignalInfo={isLoadingMapSignalInfo}
                  mappingAlerts={mappingAlerts}
                  isMappingAlertsLoading={isMappingAlertsLoading}
                  isNoRobotSelected={!(store && robot)}
                  storeLayout={storeLayout}
                  isLoadingStoreLayout={isLoadingStoreLayout}
                />
              </Grid>
              <Grid item sm={12} md={4}>
                <ActionWidget
                  store={store}
                  robot={robot}
                  topics={topics}
                  client={client}
                  actionHistory={[{}]}
                  takeRobot={takeRobot}
                  leaveRobot={leaveRobot}
                  robotsTakenList={robotsTakenList}
                  robotCode={`${store}-${robot}`}
                  getRobotActions={getRobotActions}
                  getRobotsTaken={getRobotsTaken}
                  robotActionsList={robotActionsList}
                  getRobotBlocking={getRobotBlocking}
                  takeRobotResponse={takeRobotResponse}
                  leaveRobotResponse={leaveRobotResponse}
                  isSubscribedStatus={isSubscribedStatus}
                  isLoadingTakeRobot={isLoadingTakeRobot}
                  isLoadingLeaveRobot={isLoadingLeaveRobot}
                  topicMessageFeedback={topicMessageFeedback}
                  isLoadingRobotBlocking={isLoadingRobotBlocking}
                  mqttNavMessage={mqttNavMessage}
                  mqttNavInfo={mqttNavInfo}
                  frontalImage={frontalImage}
                  postUserActions={postUserActions}
                  postActionReason={postActionReason}
                  mqttMessage={mqttMessage}
                  floorImage={floorImage}
                  productsList={productsList}
                  isLoadingProductsList={isLoadingProductsList}
                  getProductsByName={getProductsByName}
                  storeMapInfo={storeMapInfo}
                  rawRobotStatus={rawRobotStatus}
                  publishMessageCommand={publishMessageCommand}
                  iHaveRobotTaken={iHaveRobotTaken}
                  setIHaveRobotTaken={setIHaveRobotTaken}
                  takeSelectedRobot={takeSelectedRobot}
                  leaveSelectedRobot={leaveSelectedRobot}
                  controlledBy={controlledBy}
                  setOpenKick={setOpenKick}
                  detailedUserInfo={detailedUserInfo}
                  isNoRobotSelected={!(store && robot)}
                  storeLayout={storeLayout}
                  isLoadingStoreLayout={isLoadingStoreLayout}
                />
              </Grid>

              <Grid item sm={12} md={6}>
                <ScheduleWidget
                  title={t('overseer_app.widget.schedule', 'Schedule')}
                  store={store}
                  robot={robot}
                  rawRobotStatus={rawRobotStatus}
                  isNoRobotSelected={!(store && robot)}
                />
              </Grid>
              <Grid item sm={12} md={6}>
                <CallerWidget
                  callMotives={callMotives}
                  getCallerHistory={getCallerHistory}
                  callerHistory={callerHistory}
                  postCallerAction={postCallerAction}
                  loadingCallerResponse={loadingNewCallerActionResponse}
                  newCallerActionResponse={newCallerActionResponse}
                  storeInfo={storeInfo}
                  storeLang={storeLang}
                  detailedUserInfo={detailedUserInfo}
                  isNoRobotSelected={!(store && robot)}
                />
              </Grid>
            </Grid>
          )}
        </TabPanelv2>
        {/* Session Widgets */}
        <TabPanelv2 value={tabIndex} index={1} sx={{ px: 3 }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <AislesProgressWidget
                title={t(
                  'overseer_app.robot_view.aisle_progress',
                  'Aisle Progress'
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <PipelineProgressWidget store={store} robot={robot} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <SessionPlanWidget
                title={t('overseer_app.widget.session_plan', 'Session Plan')}
                store={store}
                robotCode={`${store}-${robot}`}
                isLoadingRobotBlocking={isLoadingRobotBlocking}
                isLoadingTakeRobot={isLoadingTakeRobot}
                isLoadingLeaveRobot={isLoadingLeaveRobot}
                rawRobotStatus={rawRobotStatus}
                postUserActions={postUserActions}
                publishMessageCommand={publishMessageCommand}
                iHaveRobotTaken={iHaveRobotTaken}
                setIHaveRobotTaken={setIHaveRobotTaken}
                takeSelectedRobot={takeSelectedRobot}
                leaveSelectedRobot={leaveSelectedRobot}
                controlledBy={controlledBy}
                getRobotsTaken={getRobotsTaken}
                openKick={openKick}
                setOpenKick={setOpenKick}
                robotsTakenList={robotsTakenList}
                detailedUserInfo={detailedUserInfo}
                checkFeaturePermission={checkFeaturePermission}
                featurePermission={featurePermission}
                postActionReason={postActionReason}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={8}>
              <SessionLogsWidget
                client={client}
                topics={topics}
                monitoringLogs={monitoringLogs}
                isSessionLogsLoading={isSessionLogsLoading}
                setIsSessionLogsLoading={setIsSessionLogsLoading}
              />
            </Grid>
          </Grid>
        </TabPanelv2>
        {/* Timeline */}
        <TabPanelv2 value={tabIndex} index={2} sx={{ px: 2 }}>
          <TimelineWidget
            storeInfo={storeInfo}
            getMapSignal={getMapSignal}
            getMappingAlerts={getMappingAlerts}
            store={store}
            robot={robot}
            mqttNavInfo={mqttNavInfo}
            storeMapInfo={storeMapInfo}
            minHeight={mapHeight}
            mapSignal={mapSignal}
            isLoadingMapSignalInfo={isLoadingMapSignalInfo}
            mappingAlerts={mappingAlerts}
            isMappingAlertsLoading={isMappingAlertsLoading}
            storeLayout={storeLayout}
            isLoadingStoreLayout={isLoadingStoreLayout}
          />
        </TabPanelv2>
        {/* Maintenance Widgets */}
        <TabPanelv2
          value={tabIndex}
          component={Grid}
          index={3}
          container
          spacing={{ xs: 2, md: 3 }}
        >
          <Grid item xs={12} md={6}>
            <RobotStateHistoryWidget
              store={store}
              robot={robot}
              setOpenState={setOpenState}
              featurePermission={featurePermission}
              checkFeaturePermission={checkFeaturePermission}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <HardwareAlertsWidget store={store} robot={robot} />
          </Grid>
          <Grid item xs={12} md={3}>
            <RobotParametersWidget robot_uuid={robotStateInfo?.robot_uuid} />
          </Grid>
          <Grid item xs={12} md={6}>
            <HardwareComponentsWidget store={store} robot={robot} />
          </Grid>
          <Grid item xs={12} md={6}>
            <ServicingHistoryWidget
              store={store}
              robot={robot}
              handleOpenDialog={handleOpenDialog}
            />
          </Grid>
        </TabPanelv2>

        <NewServicingDialog
          open={open}
          handleClose={handleCloseDialog}
          postNewServicing={postNewServicing}
          robotCode={`${store}-${robot}`}
          changeMotivesList={changeMotivesList}
          isLoadingChangeMotives={isLoadingChangeMotives}
          robotComponentsList={robotComponentsList}
          resetForm={resetForm}
          setResetForm={setResetForm}
          isLoadingNewServicing={isLoadingNewServicing}
        />
        <NewStateDialog
          robotCode={`${store}-${robot}`}
          robotState={robotState}
          open={openState}
          setOpenState={setOpenState}
          availableStatus={availableStatus}
          getAvailableRobotStatus={getAvailableRobotStatus}
          updateRobotState={updateRobotState}
          robotStateUpdateResponse={robotStateUpdateResponse}
          loadingNewRobotState={loadingNewRobotState}
          resetForm={resetForm}
          setResetForm={setResetForm}
          robotStateMotives={robotStateMotives}
          getSessionsState={getSessionsState}
          robotsListBaldur={robotsListBaldur}
          robotSessionState={robotSessionState}
        />
      </Grid>
      <ConfirmDialog
        open={openKick}
        handleClose={handleCloseKickDialog}
        handleConfirm={kickAndTake}
        elementObject={{
          title: t('overseer_app.widget.kick_control', 'Kick and take control'),
        }}
        isCommand={false}
      />
    </Grid>
  );
}
